import {
    Button,
    Classes,
    NumberRange,
    Popover,
    RangeSlider
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import classNames from "classnames";
import { FieldLabel } from "components/common/FieldLabel";
import { useEffect, useState } from "react";
import css from "./RangeFilterSelect.module.scss";

// Gap between the trigger and the dropdown, in px (Popper takes a number).
const DROPDOWN_OFFSET = 8;

export interface RangeFilterField {
    key: string;
    label: string;
    min: number;
    max: number;
    /**
     * Every axis tick, min and max included. Given as values rather than a
     * step size because Blueprint drops the max unless it lands exactly on a
     * step boundary, which leaves the axis ending short of the track.
     */
    labelValues: number[];
    /** Formats axis ticks and handle labels; raw numbers when omitted. */
    labelRenderer?: (value: number) => string;
    value: NumberRange;
    onChange: (value: NumberRange) => void;
}

export const isFullRange = (field: RangeFilterField): boolean =>
    field.value[0] === field.min && field.value[1] === field.max;

/** One half of the joined min / max field: formatted at rest, raw while editing. */
const RangeBoundInput: React.FC<{
    readonly label: "Min" | "Max";
    readonly ariaLabel: string;
    readonly value: number;
    readonly format: (value: number) => string;
    readonly onType: (value: number) => void;
    readonly onCommit: () => void;
}> = ({ label, ariaLabel, value, format, onType, onCommit }) => {
    const [text, setText] = useState<string>();

    return (
        <label className={css["RangeFilterSelect-bound"]}>
            <span
                className={classNames(Classes.TEXT_MUTED, Classes.TEXT_SMALL)}
            >
                {label}
            </span>
            <input
                className={classNames(
                    css["RangeFilterSelect-bound-input"],
                    label === "Max" && css["RangeFilterSelect-bound-input--end"]
                )}
                inputMode="decimal"
                aria-label={ariaLabel}
                value={text ?? format(value)}
                onFocus={(event) => {
                    const input = event.currentTarget;
                    setText(String(value));
                    // After the raw value replaces the formatted one, which moves the caret.
                    requestAnimationFrame(() => input.select());
                }}
                onChange={(event) => {
                    const next = event.target.value;
                    setText(next);
                    if (next.trim() !== "" && !isNaN(Number(next))) {
                        onType(Number(next));
                    }
                }}
                onBlur={() => {
                    setText(undefined);
                    onCommit();
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.currentTarget.blur();
                    }
                }}
            />
        </label>
    );
};

/**
 * One slider with min / max inputs, edited against local state and committed
 * on release, blur or Enter.
 *
 * Reporting every intermediate value would re-filter the table on each mouse
 * move, and a grid with flex columns re-solves its layout on every row-data
 * swap. The handles still track the pointer; only the filter waits.
 */
const RangeField: React.FC<{ field: RangeFilterField }> = ({ field }) => {
    const [draft, setDraft] = useState<NumberRange>(field.value);
    const [committedMin, committedMax] = field.value;

    useEffect(() => {
        setDraft([committedMin, committedMax]);
    }, [committedMin, committedMax]);

    const clamp = (value: number) =>
        Math.min(field.max, Math.max(field.min, Math.round(value)));
    // Typed bounds can sit outside the span or cross until committed; the slider only ever sees a valid range.
    const inSpan: NumberRange = [
        clamp(Math.min(...draft)),
        clamp(Math.max(...draft))
    ];
    // An unchanged commit would store [min, max] over a null no-filter state,
    // which a later wider axis reads as a narrowed filter.
    const commit = () => {
        setDraft(inSpan);
        if (inSpan[0] !== committedMin || inSpan[1] !== committedMax) {
            field.onChange(inSpan);
        }
    };
    const format = field.labelRenderer ?? String;
    const boundInput = (index: 0 | 1) => (
        <RangeBoundInput
            label={index === 0 ? "Min" : "Max"}
            ariaLabel={`${field.label} ${index === 0 ? "min" : "max"}`}
            value={draft[index]}
            format={format}
            onType={(value) =>
                setDraft(index === 0 ? [value, draft[1]] : [draft[0], value])
            }
            onCommit={commit}
        />
    );

    return (
        <div className={css["RangeFilterSelect-popover-field"]}>
            <FieldLabel className={css["RangeFilterSelect-popover-label"]}>
                {field.label}
            </FieldLabel>
            <div className={css["RangeFilterSelect-popover-slider"]}>
                <RangeSlider
                    min={field.min}
                    max={field.max}
                    labelValues={field.labelValues}
                    labelRenderer={field.labelRenderer}
                    value={inSpan}
                    onChange={setDraft}
                    onRelease={field.onChange}
                />
            </div>
            <div className={css["RangeFilterSelect-bounds"]}>
                {boundInput(0)}
                <span className={css["RangeFilterSelect-bounds-divider"]} />
                {boundInput(1)}
            </div>
        </div>
    );
};

/**
 * Numeric range facet for a table ribbon.
 *
 * A field left at its full span means "no filter", so callers can treat it as
 * pass-everything rather than hide-everything.
 */
export const RangeFilterSelect: React.FC<{
    label: string;
    fields: ReadonlyArray<RangeFilterField>;
}> = ({ label, fields }) => {
    const narrowedCount = fields.filter((field) => !isFullRange(field)).length;

    const popoverContent = (
        <div className={css["RangeFilterSelect-popover"]}>
            <div className={css["RangeFilterSelect-popover-header"]}>
                <span className={css["RangeFilterSelect-popover-title"]}>
                    {label}
                </span>
                <Button
                    variant="minimal"
                    size="small"
                    intent="primary"
                    disabled={narrowedCount === 0}
                    onClick={() =>
                        fields.forEach((field) =>
                            field.onChange([field.min, field.max])
                        )
                    }
                >
                    Reset
                </Button>
            </div>
            {fields.map((field) => (
                <RangeField key={field.key} field={field} />
            ))}
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            placement="bottom-start"
            popoverClassName={css["RangeFilterSelect-dropdown"]}
            modifiers={{
                offset: {
                    enabled: true,
                    options: { offset: [0, DROPDOWN_OFFSET] }
                }
            }}
            minimal
        >
            <Button
                className={css["RangeFilterSelect-button"]}
                endIcon={IconNames.CHEVRON_DOWN}
                intent={narrowedCount > 0 ? "primary" : "none"}
                variant="outlined"
            >
                {label}
                <span
                    className={css["RangeFilterSelect-badge"]}
                    // Always laid out, so applying a filter cannot resize the
                    // trigger and shuffle the controls beside it.
                    style={{
                        visibility: narrowedCount > 0 ? "visible" : "hidden"
                    }}
                >
                    {narrowedCount}
                </span>
            </Button>
        </Popover>
    );
};
